import * as React from 'react';
import { ReactNode } from 'react';
import {
  ColorValue,
  Platform,
  StyleSheet,
  Text,
  TextProps,
  TouchableOpacity,
} from 'react-native';
import PlatformColor from './PlatformColor';
import useDialogTheme, { StyleBuilder } from './useTheme';

const COLOR = Platform.OS === 'android' ? '#169689' : '#007ff9';

export interface DialogButtonProps extends TextProps {
  label: ReactNode;
  color?: ColorValue;
  bold?: boolean;
  disabled?: boolean;
  destructive?: boolean;
  onPress: () => void;
}

const DialogButton: React.FC<DialogButtonProps> = props => {
  const {
    label,
    color = COLOR,
    disabled = false,
    destructive = false,
    bold,
    onPress,
    style,
    ...nodeProps
  } = props;
  const fontWeight = bold ? '600' : 'normal';
  const { styles } = useDialogTheme(buildStyles);

  return (
    <TouchableOpacity
      style={[styles.button]}
      onPress={onPress}
      disabled={disabled}
    >
      <Text
        style={[
          styles.text,
          style,
          {
            color: destructive
              ? (Platform.select({
                  default: PlatformColor('systemRed'),
                  android: PlatformColor('@android:color/system_error_600'),
                }) as any)
              : color,
            fontWeight: fontWeight,
          },
        ]}
        {...nodeProps}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
};

DialogButton.displayName = 'DialogButton';

const buildStyles: StyleBuilder = isDark =>
  StyleSheet.create({
    button: Platform.select({
      default: {
        flexGrow: 1,
        flexShrink: 1,
        height: 46,
        justifyContent: 'center',
        alignItems: 'center',
      },
      android: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    }),
    text: Platform.select({
      default: {
        color: PlatformColor('link'),
        textAlign: 'center',
        fontSize: 17,
        backgroundColor: 'transparent',
      },
      android: {
        color: PlatformColor(
          `@android:color/${
            isDark ? 'link_text_dark' : 'link_text_dark_light'
          }`,
        ),
        textAlign: 'center',
        backgroundColor: 'transparent',
        padding: 8,
        fontSize: 14,
        textTransform: 'uppercase',
      },
    }),
  });

export default DialogButton;
